import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachScannerCaseListItem from './attach-scanner-case-list-item.component';
import { attachCaseSearchTemplate } from 'apollo/templates/scanner-case';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import { LoadingButton } from '@mui/lab';
import { ScannerCase } from 'models/scanner-case';

type AttachScannerCaseSearchProps = {
    orderId: string;
    pin: string;
    name: string;
    onAttach: (scannerCase: ScannerCase) => void;
};
const AttachScannerCaseSearch: FC<AttachScannerCaseSearchProps> = ({ orderId, onAttach, pin, name }) => {
    const { t: translate } = useTranslation(['common', 'scan-cases']);
    const [expanded, setExpanded] = React.useState<number | false>(false);
    const [lastOffset, setLastOffset] = useState(0);
    const [scannerCases, setScannerCases] = useState<ScannerCase[]>([]);
    const [listType, setListType] = useState('');
    const [searchFilters, setSearchFilters] = useState<{ name: string; pin: string }>({ name: '', pin: '' });
    const { showBoundary } = useDenthubErrorBoundary();
    const [showLoadMoreButton, setShowLoadMoreButton] = useState(true);

    const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleEnter = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            searchScannerCases();
        }
    };

    const [attachCaseSearch, { loading, fetchMore, networkStatus }] = useLazyQuery(attachCaseSearchTemplate, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        pollInterval: 1 * 60 * 1000,
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ attachCaseSearch: attachCaseSearch }) => {
            setShowLoadMoreButton(attachCaseSearch?.scannerCases.length - scannerCases.length === 10);
            setScannerCases(attachCaseSearch?.scannerCases || []);
            setListType(attachCaseSearch?.listType || 'NONE');
            setLastOffset(attachCaseSearch?.scannerCases.length || 0);
            if (attachCaseSearch?.listType == 'PIN') {
                setSearchFilters({ ...searchFilters, pin: pin });
            } else if (attachCaseSearch?.listType == 'NAME') {
                setSearchFilters({ ...searchFilters, name: name });
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        attachCaseSearch({
            variables: {
                limit: 10,
                offset: 0,
                orderId: orderId,
            },
        });
    }, [orderId]);

    const handleFilterChange = (event: ChangeEvent<HTMLInputElement>, type: string) => {
        const value = event.target.value;
        setSearchFilters({ ...searchFilters, [type]: value });
    };

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        } else {
            attachCaseSearch({
                variables: {
                    limit: 10,
                    offset: 0,
                    orderId: orderId,
                    ...searchFilters,
                },
            });
        }
    };

    const onAttachScannerCase = (scannerCase: ScannerCase) => {
        onAttach(scannerCase);
    };

    const searchScannerCases = () => {
        setShowLoadMoreButton(true);
        setScannerCases([]);
        setLastOffset(0);
        attachCaseSearch({
            variables: {
                limit: 10,
                offset: 0,
                orderId: orderId,
                ...searchFilters,
            },
        });
    };

    return (
        <Fragment>
            <Box mb={2} display="flex" alignItems="center" columnGap={1} mt={2}>
                <TextField
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleFilterChange(event, 'pin')}
                    fullWidth
                    value={searchFilters.pin}
                    label={translate('scan-cases:PIN_SEARCH')}
                    onKeyDown={handleEnter}
                />
                <TextField
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleFilterChange(event, 'name')}
                    fullWidth
                    value={searchFilters.name}
                    label={translate('scan-cases:NAME_SEARCH')}
                    onKeyDown={handleEnter}
                />
                <Button variant="contained" onClick={searchScannerCases}>
                    {translate('common:SEARCH')}
                </Button>
            </Box>
            {listType === 'NONE' ? (
                <Box mb={2}>
                    <Alert severity="info">{translate('scan-cases:NO_SCAN_CASE_MATCH')}</Alert>
                </Box>
            ) : undefined}
            {loading && networkStatus !== NetworkStatus.fetchMore && networkStatus !== NetworkStatus.poll ? (
                <Loader />
            ) : (
                <Fragment>
                    {scannerCases.map((scannerCase, i) => (
                        <Accordion
                            key={i}
                            sx={{ border: '1px solid #ddd' }}
                            elevation={0}
                            expanded={expanded === i}
                            onChange={handleChange(i)}
                        >
                            <AccordionSummary
                                sx={{ background: '#f1f1f1', borderTopColor: '#ddd' }}
                                expandIcon={<ExpandMoreIcon />}
                            >
                                <Typography sx={{ width: 155 }}>
                                    {formatDate(scannerCase.updatedAt, 'datetime')}
                                </Typography>
                                <Typography sx={{ width: '75%' }}>
                                    {scannerCase.patient?.firstName} {scannerCase.patient?.lastName} -{' '}
                                    {scannerCase.patient?.patientRefNo}
                                </Typography>
                                <Box ml={2}>{translate(`scan-cases:${scannerCase.source}`)}</Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {expanded === i ? (
                                    <AttachScannerCaseListItem
                                        orderId={orderId}
                                        onAttachScannerCase={onAttachScannerCase}
                                        scannerCase={scannerCase}
                                    />
                                ) : undefined}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    {scannerCases?.length > 0 && showLoadMoreButton && (
                        <Box mt={2} sx={{ textAlign: 'right' }}>
                            <LoadingButton variant="contained" onClick={handleFetchMore} loading={loading}>
                                {translate('scan-cases:FETCH_MORE')}
                            </LoadingButton>
                        </Box>
                    )}

                    {scannerCases?.length === 0 && (
                        <Box mt={2} sx={{ textAlign: 'right' }}>
                            <Alert severity="info">{translate('scan-cases:NO_SCAN_CASES_FOUND')}</Alert>
                        </Box>
                    )}
                </Fragment>
            )}
        </Fragment>
    );
};

export default AttachScannerCaseSearch;
